import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Grid, Card } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ContactForm from '../components/forms/ContactForm';
import GlanceCard from '../components/GlanceCard';
import PageWrapper from '../components/PageWrapper';
import SectionDivider from '../components/SectionDivider';
import VideoModal, { shouldShowVideoModal } from '../components/VideoModal';
import CornerVideoPopup from '../components/CornerVideoPopup';
import useConfig from '../hooks/useConfig';

const Home = () => {
  const [showCornerPopup, setShowCornerPopup] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [isFromCornerPopup, setIsFromCornerPopup] = useState(false);
  const { config: openHouseConfig } = useConfig('open-house');
  const { config: homeConfig } = useConfig('home');

  useEffect(() => {
    if (homeConfig?.videoModal?.enabled && openHouseConfig && shouldShowVideoModal(homeConfig.videoModal.cooldownDays)) {
      const delayMs = (homeConfig.videoModal.delaySeconds || 2.5) * 1000;
      const timer = setTimeout(() => {
        setShowCornerPopup(true);
      }, delayMs);

      return () => clearTimeout(timer);
    }
  }, [homeConfig, openHouseConfig]);

  const handleCloseVideoModal = () => {
    setShowVideoModal(false);
    setIsFromCornerPopup(false);
  };

  const handleCloseCornerPopup = () => {
    setShowCornerPopup(false);
    // Set cooldown when corner popup is closed
    localStorage.setItem('mga-video-modal-last-shown', Date.now().toString());
  };

  const handleCornerPopupClick = () => {
    setShowCornerPopup(false);
    setIsFromCornerPopup(true);
    setShowVideoModal(true);
  };

  return (
    <PageWrapper configKey="home" pageTitle="MacGillis Global Academy">
      {(config) => (
        <>
          {/* Welcome Section */}
          <Box sx={{ py: 4 }}>
            <Box sx={{ px: { xs: 2, sm: 3 } }}>
              <Container maxWidth="lg">
                <Typography variant="h5" component="p" sx={{ mb: 3 }}>
                  As an accredited, independent online school, <b>MacGillis Global Academy (MGA)</b> is dedicated to bringing live, premium, and authentic education that inspires students to develop their passions to the fullest extent of their imaginations in a safe, supportive environment.
                </Typography>
                <Typography variant="h5" component="p">
                  Join our learning community and give your child the education they deserve!
                </Typography>
              </Container>
            </Box>
          </Box>

          <SectionDivider />

          {/* MGA at a Glance Section */}
          <Box sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
            <Container maxWidth="lg">
              <Typography variant="h3" component="h2" gutterBottom sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                MGA at a Glance
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{
                  width: {
                    md: 'calc(25% - 18px)',
                    sm: 'calc(50% - 18px)',
                    xs: 'calc(100% - 18px)'
                  }
                }}
                >
                  <GlanceCard
                    number="1"
                    title="global community of learners"
                    description="Welcomes scholars from all over the world"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{
                  width: {
                    md: 'calc(25% - 18px)',
                    sm: 'calc(50% - 18px)',
                    xs: 'calc(100% - 18px)'
                  }
                }}
                >
                  <GlanceCard
                    number="10-16"
                    title="students per class"
                    description="Small class sizes for personalized attention"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{
                  width: {
                    md: 'calc(25% - 18px)',
                    sm: 'calc(50% - 18px)',
                    xs: 'calc(100% - 18px)'
                  }
                }}
                >
                  <GlanceCard
                    number="100%"
                    title="live, interactive online instruction"
                    description="Engaging discussions, no pre-recorded lessons"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{
                  width: {
                    md: 'calc(25% - 18px)',
                    sm: 'calc(50% - 18px)',
                    xs: 'calc(100% - 18px)'
                  }
                }}
                >
                  <GlanceCard
                    number="100%"
                    title="licensed instructors or college faculty"
                    description="Seasoned master teachers with advanced degrees, averaging 20+ years of teaching experience"
                  />
                </Grid>
              </Grid>
            </Container>
          </Box>

          <SectionDivider />

          {/* Contact Form Section */}
          <Box sx={{ pt: 4, pb: 1 }}>
            <Box sx={{ px: { xs: 2, sm: 3 } }}>
              <Container maxWidth="lg">
                <Typography variant="h3" component="h2" gutterBottom sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                  Contact Us
                </Typography>

                {/* Contact Information Cards */}
                <Grid container spacing={3} sx={{ mb: 3 }} alignItems="stretch">
                  <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
                    <Card sx={{ width: '100%', textAlign: 'center', px: 2, py: 2, bgcolor: '#FFF9C4', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <LocationOnIcon sx={{ fontSize: 40, color: 'primary.dark', mb: 1 }} />
                      <Typography variant="h6" gutterBottom>
                        Address
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {config.contact.address.line1}<br />
                        {config.contact.address.line2}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
                    <Card sx={{ width: '100%', textAlign: 'center', px: 2, py: 2, bgcolor: '#C8E6C9', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <EmailIcon sx={{ fontSize: 40, color: 'primary.dark', mb: 1 }} />
                      <Typography variant="h6" gutterBottom>
                        Email Address
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        <a href={`mailto:${config.contact.email}`}>{config.contact.email}</a>
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
                    <Card sx={{ width: '100%', textAlign: 'center', px: 2, py: 2, bgcolor: '#BBDEFB', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <PhoneIcon sx={{ fontSize: 40, color: 'primary.dark', mb: 1 }} />
                      <Typography variant="h6" gutterBottom>
                        Phone Number
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {config.contact.phone}
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>

                <ContactForm />
              </Container>
            </Box>
          </Box>
          {/* Corner Video Popup */}
          {openHouseConfig && homeConfig?.videoModal?.enabled && (
            <CornerVideoPopup
              open={showCornerPopup}
              onClose={handleCloseCornerPopup}
              onVideoClick={handleCornerPopupClick}
              size={homeConfig.videoModal.cornerPopup?.size || { width: 200, height: 300 }}
            />
          )}

          {/* Video Modal */}
          {openHouseConfig && homeConfig?.videoModal?.enabled && (
            <VideoModal
              open={showVideoModal}
              onClose={handleCloseVideoModal}
              events={openHouseConfig.events || []}
              timeZone={openHouseConfig.timeZone}
              openHouseEmail={openHouseConfig.openHouseEmail}
              cooldownDays={homeConfig.videoModal.cooldownDays || 7}
              isFromCornerPopup={isFromCornerPopup}
            />
          )}
        </>
      )}
    </PageWrapper>
  );
};

export default Home; 